<script setup lang="ts">
import { getRepairRequest, getRepairRequestDocument, updateRepairRequestStatus } from '@/services/rc.service'
import { Edit } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, ref, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import { RepairRequestStatus } from '@/types/rc/repair-requests'

// router
const router = useRouter()

// props
const props = defineProps<{ request: any; expanded: boolean }>()
const { expanded } = toRefs(props)

// emit
const emit = defineEmits(['itemExpanded', 'editRepairRequest', 'statusUpdated'])

// computed
const activeStatus = computed(
  () =>
    props.request.status === RepairRequestStatus.UnderReview ||
    props.request.status === RepairRequestStatus.Approved ||
    props.request.status === RepairRequestStatus.Complete ||
    props.request.status === RepairRequestStatus.Denied
)

// refs
const openingDocument = ref<boolean>(false)
const updatingStatus = ref<string>()

// Vue Query
const queryClient = useQueryClient()

const { data } = useQuery({
  queryKey: ['repair-request-details', props.request.number],
  queryFn: () => getRepairRequest(props.request.number),
  enabled: expanded,
})

const updateStatus = useMutation({
  mutationFn: ({ id, variables }: { id: string; variables: { status: string } }) =>
    updateRepairRequestStatus(id, variables.status),
  onSuccess: (updated: any) => {
    emit('statusUpdated', updated)
  },
})

/**
 * Opens a repair request document in a new tab.
 *
 * @param documentId the ID of the document to open.
 */
const openDocument = async (documentId: number) => {
  openingDocument.value = true
  let newTab
  try {
    newTab = window.open()
    if (!newTab) {
      ElMessage({
        type: 'error',
        message: 'Failed to open a new tab, check your browser settings.',
      })
      return
    }
    newTab.document.write('Loading RSR document...')
    const data = await getRepairRequestDocument(props.request.number, documentId)
    const fileUrl = window.URL.createObjectURL(data)
    newTab.location.assign(fileUrl)
    URL.revokeObjectURL(fileUrl)
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to retrieve RSR.',
    })
    newTab?.close()
  }
  openingDocument.value = false
}

/**
 * Will open work order, not yet implemented.
 */
const openWorkOrder = async (workOrderNumber: string) => {
  router.push({ name: 'rc-work-order-dashboard', params: { number: workOrderNumber } })
}

/**
 * Handle status update emission from repair request status actions.
 */
const handleStatusUpdate = async (newStatus: string) => {
  try {
    await updateStatus.mutateAsync({ id: props.request.number, variables: { status: newStatus } })
    queryClient.invalidateQueries({ queryKey: ['repair-request-details', props.request.number] })
  } catch (err: any) {
    ElMessage({
      type: 'error',
      message: err.message,
    })
  } finally {
    updatingStatus.value = ''
  }
}

/**
 * Handles edit button click (emits to parent to open dialog).
 */
const handleEdit = (e: Event) => {
  e.stopPropagation() // Prevent item expansion.
  emit('editRepairRequest', props.request.number)
}
</script>

<template>
  <el-card shadow="never">
    <el-collapse-item v-if="request" :name="request.number">
      <template #title>
        <header>
          <div class="repair-request__title">
            <h4 style="min-width: 148px">{{ request.type }}</h4>
            <CopyButton :value="request.number"
              ><h4 style="width: 52px">#{{ request.number.slice(-4) }}</h4></CopyButton
            >
          </div>
          <div class="repair-request__controls">
            <repair-request-status-actions
              v-if="activeStatus"
              v-model="updatingStatus"
              :repair-request-number="request.number"
              :status="request.status"
              @status-action="handleStatusUpdate"
            />
            <StatusBadge :status="request.status" />
            <el-button v-if="activeStatus" link :icon="Edit" @click="handleEdit"></el-button>
          </div>
        </header>
      </template>
      <el-divider />
      <el-descriptions :column="1" border>
        <el-descriptions-item label="Request date">
          <span v-if="data?.request_date"> {{ DateTime.fromISO(data.request_date)?.toLocaleString() }} </span
          ><Icon v-else icon="eos-icons:three-dots-loading" :height="20"
        /></el-descriptions-item>
        <el-descriptions-item label="Type"
          ><span v-if="data?.type">{{ data.type }}</span>
          <Icon v-else icon="eos-icons:three-dots-loading" :height="20" />
        </el-descriptions-item>
        <el-descriptions-item label="Miners to repair"
          ><span v-if="data?.expected_units">{{ data.expected_units }}</span>
          <Icon v-else icon="eos-icons:three-dots-loading" />
        </el-descriptions-item>
        <el-descriptions-item label="Miners families">
          <div class="flex flex-wrap gap-2">
            <el-tag v-for="model in data?.miner_models" :key="model" size="small">{{ model }}</el-tag>
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="Scope of work">
          <rc-scope-of-work v-if="data?.scope_of_work" :scope-of-work="data.scope_of_work" />
          <Icon v-else icon="eos-icons:three-dots-loading" :height="20" />
        </el-descriptions-item>
        <el-descriptions-item v-if="data?.custom_repair_fees?.length > 0" label="Custom repair fees">
          <table>
            <tr v-for="fee in data.custom_repair_fees" :key="fee.repair_action">
              <th>{{ fee.repair_action.action }} &ndash; {{ fee.repair_action.part_type }}</th>
              <td><span style="color: var(--text-tertiary)">$</span> {{ fee.price }}</td>
            </tr>
          </table>
        </el-descriptions-item>
      </el-descriptions>
      <section class="links">
        <template v-if="data?.documents && data?.documents.length > 0">
          <div v-for="doc in data.documents" :key="doc.id" class="link" @click="openDocument(doc.id)">
            RSR &ndash; {{ doc.name }}
            <Icon v-if="openingDocument" icon="eos-icons:three-dots-loading" :height="20" />
            <Icon v-else icon="material-symbols:open-in-new" :height="20" />
          </div>
        </template>
        <div v-if="data?.work_order_number" class="link" @click="openWorkOrder(data.work_order_number)">
          Work Order <Icon icon="material-symbols:open-in-new" :height="20" />
        </div>
      </section>
    </el-collapse-item>
  </el-card>
</template>

<style scoped lang="scss">
header {
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  color: var(--text-secondary);
}

.repair-request {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-spacing-xs);
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: var(--spacing-spacing-md);
  }
}

th {
  font-weight: normal;
  width: 256px;
}

td {
  width: 86px;
}

li {
  display: flex;
  align-items: center;
  gap: 8px;
}

.excluded-repair-action {
  color: var(--text-tertiary);
}

.links {
  margin-top: var(--spacing-spacing-lg);
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.link {
  flex: 1;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--fill-tertiary);
  padding: var(--spacing-spacing-sm);
  border-radius: var(--spacing-spacing-2xs);
  user-select: none;
  cursor: pointer;
}

/* Override Element Plus Collapse styles */
::v-deep(.el-collapse-item__wrap) {
  border-bottom: none;
}

::v-deep(.el-collapse-item__content) {
  font-size: var(--font-size-body-compact);
  padding-bottom: 0;
}

::v-deep(.el-collapse-item__header) {
  border: none;
  display: flex;
  flex-direction: row-reverse;
  height: 32px;
}

/* End Override */

/* Override Element Plus Bordered Descriptions (the borderless option removes tabular layout). */
::v-deep(.el-descriptions__label.el-descriptions__cell.is-bordered-label) {
  display: table-cell;
  vertical-align: top;
  color: var(--text-tertiary);
  background-color: inherit;
  width: 256px;
  font-weight: normal;
}

::v-deep(.el-descriptions__body .el-descriptions__table.is-bordered .el-descriptions__cell) {
  border: 0;
}

/* End override */
</style>
